import React, { useState, useEffect, useRef, useCallback } from 'react';
import './MapControls.css';

const SvgMapIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline-block', verticalAlign:'middle'}}>
    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
    <line x1="8" y1="2" x2="8" y2="18"></line>
    <line x1="16" y1="6" x2="16" y2="22"></line>
  </svg>
);

const SvgSatelliteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline-block', verticalAlign:'middle'}}>
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="2" y1="12" x2="22" y2="12"></line>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
  </svg>
);

const SvgTopoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline-block', verticalAlign:'middle'}}>
    <path d="m8 3 4 8 5-5 5 15H2L8 3z"></path>
  </svg>
);

const SvgLightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline-block', verticalAlign:'middle'}}>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="3" y1="9" x2="21" y2="9"></line>
    <line x1="9" y1="21" x2="9" y2="9"></line>
  </svg>
);

/**
 * Definições dos fornecedores de mosaicos (Tile Layers) suportados pelo SARIA 2.0
 */
export const BASE_TILE_LAYERS = {
  osm: {
    id: 'osm',
    name: 'Cartografia',
    shortName: 'Mapa',
    icon: <SvgMapIcon />,
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    options: {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>'
    }
  },
  satellite: {
    id: 'satellite',
    name: 'Satélite',
    shortName: 'Satélite',
    icon: <SvgSatelliteIcon />,
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    options: {
      maxZoom: 19,
      attribution: 'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, GIS Community'
    }
  },
  topo: {
    id: 'topo',
    name: 'Topográfico',
    shortName: 'Topo',
    icon: <SvgTopoIcon />,
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    options: {
      maxZoom: 17,
      attribution: 'Map: &copy; <a href="https://opentopomap.org" target="_blank" rel="noreferrer">OpenTopoMap</a>, SRTM | Data: OSM'
    }
  },
  light: {
    id: 'light',
    name: 'Claro / Alto Contraste',
    shortName: 'Claro',
    icon: <SvgLightIcon />,
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
    options: {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions" target="_blank" rel="noreferrer">CARTO</a>'
    }
  }
};

/**
 * Inicializa e gere as camadas base Leaflet num mapa existente
 */
export function setupMapTileLayers(mapInstance, defaultLayerKey = 'osm') {
  if (!mapInstance || typeof window === 'undefined' || !window.L) return null;
  const L = window.L;

  const createdLayers = {};
  const baseMapsForControl = {};

  Object.entries(BASE_TILE_LAYERS).forEach(([key, config]) => {
    const tileLayer = L.tileLayer(config.url, config.options);
    createdLayers[key] = tileLayer;
    baseMapsForControl[config.name] = tileLayer;
  });

  const initialKey = BASE_TILE_LAYERS[defaultLayerKey] ? defaultLayerKey : 'osm';
  let currentActiveKey = initialKey;
  createdLayers[initialKey].addTo(mapInstance);

  let layerControl = null;
  try {
    layerControl = L.control.layers(baseMapsForControl, null, { position: 'topright', collapsed: true });
    layerControl.addTo(mapInstance);
  } catch (e) {
    console.warn('Não foi possível adicionar L.control.layers:', e);
  }

  // Listener para sincronizar caso o utilizador mude através do controlo nativo Leaflet
  mapInstance.on('baselayerchange', (e) => {
    const foundEntry = Object.entries(BASE_TILE_LAYERS).find(([, cfg]) => cfg.name === e.name);
    if (foundEntry) {
      currentActiveKey = foundEntry[0];
    }
  });

  const switchLayer = (targetKey) => {
    if (!createdLayers[targetKey] || targetKey === currentActiveKey) return;
    try {
      if (createdLayers[currentActiveKey] && mapInstance.hasLayer(createdLayers[currentActiveKey])) {
        mapInstance.removeLayer(createdLayers[currentActiveKey]);
      }
      createdLayers[targetKey].addTo(mapInstance);
      createdLayers[targetKey].bringToBack();
      currentActiveKey = targetKey;
    } catch (err) {
      console.error('Erro ao trocar camada base do mapa:', err);
    }
  };

  return {
    switchLayer,
    getActiveKey: () => currentActiveKey,
    getLayers: () => createdLayers,
    remove: () => {
      try {
        if (layerControl) layerControl.remove();
        Object.values(createdLayers).forEach(l => {
          if (mapInstance.hasLayer(l)) mapInstance.removeLayer(l);
        });
      } catch (e) {}
    }
  };
}

/**
 * Seletor visual em botões tipo "pill" para alternar o fundo do mapa (Base Layer Switcher)
 */
export function MapLayerSwitcher({ activeLayer = 'osm', onLayerChange, className = '' }) {
  return (
    <div className={`map-layer-switcher-pills ${className}`} role="group" aria-label="Alterar fundo do mapa">
      {Object.entries(BASE_TILE_LAYERS).map(([key, cfg]) => {
        const isActive = activeLayer === key;
        return (
          <button
            key={key}
            type="button"
            className={`map-layer-pill ${isActive ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onLayerChange) onLayerChange(key);
            }}
            title={`Mudar para fundo: ${cfg.name}`}
            aria-pressed={isActive}
          >
            <span className="pill-icon" aria-hidden="true">{cfg.icon}</span>
            <span className="pill-text">{cfg.shortName}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Barra de pesquisa de moradas, localidades e coordenadas GPS para mapas
 */
export function MapSearchBar({
  map = null,
  onSelectLocation = null,
  placeholder = 'Pesquisar morada, localidade ou coordenadas GPS (ex: 39.91, -8.62)...',
  className = ''
}) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const searchContainerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('touchstart', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('touchstart', handleDocumentClick);
    };
  }, []);

  // Fechar no Escape
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setDropdownOpen(false);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      executeSearch(query, true);
    }
  };

  const handleChooseLocation = useCallback((loc) => {
    setQuery(loc.display_name || `${loc.lat}, ${loc.lon}`);
    setDropdownOpen(false);

    if (map) {
      try {
        map.setView([loc.lat, loc.lon], 15, { animate: true });
      } catch (e) {
        console.warn('Erro ao centrar mapa:', e);
      }
    }

    if (onSelectLocation) {
      onSelectLocation(loc);
    }
  }, [map, onSelectLocation]);

  const executeSearch = useCallback(async (searchQuery, isExplicitSubmit = false) => {
    const trimmed = (searchQuery || '').trim();
    if (!trimmed || trimmed.length < 2) {
      setResults([]);
      setDropdownOpen(false);
      return;
    }

    // 1. Deteção de coordenadas decimais instantâneas
    const coordRegex = /^\s*([+-]?\d+(?:[.,]\d+)?)\s*[,;\s]\s*([+-]?\d+(?:[.,]\d+)?)\s*$/;
    const coordMatch = trimmed.match(coordRegex);
    if (coordMatch) {
      const latVal = parseFloat(coordMatch[1].replace(',', '.'));
      const lonVal = parseFloat(coordMatch[2].replace(',', '.'));
      if (Number.isFinite(latVal) && Number.isFinite(lonVal) && latVal >= -90 && latVal <= 90 && lonVal >= -180 && lonVal <= 180) {
        const item = {
          display_name: `Coordenadas GPS (${latVal.toFixed(6)}, ${lonVal.toFixed(6)})`,
          lat: latVal,
          lon: lonVal,
          freguesia: '',
          concelho: '',
          type: 'coordinates'
        };
        setResults([item]);
        setStatusMessage('Coordenadas válidas');
        if (isExplicitSubmit) {
          handleChooseLocation(item);
        } else {
          setDropdownOpen(true);
        }
        return;
      }
    }

    setLoading(true);
    setStatusMessage('A pesquisar...');

    try {
      // Tenta endpoint do backend SARIA
      const apiUrl = (process.env.REACT_APP_API_URL || '') + `/api/geocode/search?q=${encodeURIComponent(trimmed)}`;
      let res = await fetch(apiUrl);
      let data = res.ok ? await res.json() : null;

      let foundItems = [];
      if (data && data.success && Array.isArray(data.results)) {
        foundItems = data.results;
      } else {
        // Fallback direto para Nominatim OpenStreetMap (se o backend estiver offline ou sem dados)
        const nominatimUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(trimmed)}&countrycodes=pt&limit=5&addressdetails=1`;
        const nRes = await fetch(nominatimUrl, { headers: { 'User-Agent': 'SARIA-System/1.0' } });
        if (nRes.ok) {
          const nData = await nRes.json();
          foundItems = (nData || []).map(it => {
            const addr = it.address || {};
            const freg = addr.parish || addr.suburb || addr.neighbourhood || addr.village || addr.hamlet || addr.locality || '';
            const conc = addr.municipality || addr.city || addr.town || addr.county || '';
            return {
              display_name: it.display_name,
              lat: parseFloat(it.lat),
              lon: parseFloat(it.lon),
              freguesia: freg,
              concelho: conc,
              type: it.type || 'place'
            };
          }).filter(r => Number.isFinite(r.lat) && Number.isFinite(r.lon));
        }
      }

      setResults(foundItems);
      if (foundItems.length > 0) {
        setStatusMessage(`${foundItems.length} resultado(s)`);
        setDropdownOpen(true);
        // Se submissão explícita (clique no botão ou Enter) e apenas 1 resultado, seleciona diretamente
        if (isExplicitSubmit && foundItems.length === 1) {
          handleChooseLocation(foundItems[0]);
        }
      } else {
        setStatusMessage('Nenhum local encontrado');
        setDropdownOpen(true);
      }
    } catch (err) {
      console.warn('Erro na pesquisa de localização:', err);
      setStatusMessage('Erro na pesquisa');
    } finally {
      setLoading(false);
    }
  }, [handleChooseLocation]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setStatusMessage('');

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (!val || val.trim().length < 3) {
      setResults([]);
      setDropdownOpen(false);
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      executeSearch(val, false);
    }, 450);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setDropdownOpen(false);
    setStatusMessage('');
  };

  return (
    <div className={`map-search-bar-wrapper ${className}`} ref={searchContainerRef}>
      <div className="map-search-input-box">
        <span className="search-icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline-block', verticalAlign:'middle'}}>
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        </span>
        <input
          type="text"
          className="map-search-input"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results.length > 0) setDropdownOpen(true); }}
          placeholder={placeholder}
          aria-label="Pesquisar localização no mapa"
          autoComplete="off"
        />
        {query && (
          <button
            type="button"
            className="search-clear-btn"
            onClick={handleClear}
            title="Limpar pesquisa"
            aria-label="Limpar pesquisa"
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
        <button
          type="button"
          className="search-submit-btn"
          onClick={() => executeSearch(query, true)}
          disabled={loading || !query.trim()}
          title="Pesquisar localização"
          aria-label="Pesquisar"
        >
          {loading ? <span className="search-spinner" /> : 'Pesquisar'}
        </button>
      </div>

      {dropdownOpen && (
        <div className="map-search-dropdown" role="listbox">
          {results.length > 0 ? (
            results.map((loc, idx) => (
              <div
                key={`${loc.lat}-${loc.lon}-${idx}`}
                className="map-search-item"
                role="option"
                aria-selected={false}
                tabIndex={0}
                onClick={() => handleChooseLocation(loc)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleChooseLocation(loc); }}
              >
                <div className="item-icon" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline-block', verticalAlign:'middle'}}>
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                <div className="item-details">
                  <div className="item-name">{loc.display_name}</div>
                  <div className="item-sub">
                    {loc.freguesia ? <span>{loc.freguesia}</span> : null}
                    {loc.concelho ? <span>{loc.freguesia ? ' · ' : ''}{loc.concelho}</span> : null}
                    <span className="item-coords">[{loc.lat.toFixed(5)}, {loc.lon.toFixed(5)}]</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="map-search-empty">
              {statusMessage || 'Nenhum local encontrado para esta pesquisa.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
